/**
 * Onboarding CTA Definitions and Selection Logic
 * Defines all contextual CTAs with provider-specific ATS connections
 */

import { CTACard, DashboardMetrics, UserState, UserContext } from './dashboardTypes';
import { isDismissed, dismissCTA } from './ctaDismissal';

export interface CTACallbacks {
  onConnectBullhorn?: () => void;
  onConnectJobAdder?: () => void;
  onAddRole?: () => void;
  onAddCandidate?: () => void;
  onReplyMessages?: () => void;
  onReviewMatches?: () => void;
  onShareRole?: () => void;
  onInviteUser?: () => void;
  onCompleteProfile?: () => void;
  onSetPreferences?: () => void;
  onExploreMarketplace?: () => void;
  onTakeTour?: () => void;
  onViewHelp?: () => void;
}

/**
 * Create all CTA definitions with provider-specific ATS CTAs
 *
 * NOTE: For new_user state, CTAs focus exclusively on ATS onboarding:
 * - ATS connections (Bullhorn, JobAdder) are top priority
 * - Manual data entry (add role/candidate) is secondary
 * - Messages and matches CTAs are intentionally excluded to avoid distraction
 */
export function createCTADefinitions(callbacks: CTACallbacks): CTACard[] {
  return [
    // Priority 1a: Connect Bullhorn (highest priority for new user onboarding)
    {
      id: 'connect-bullhorn',
      type: 'primary',
      title: 'Connect Bullhorn',
      description: 'Import your roles and candidates from Bullhorn to get started quickly',
      actionLabel: 'Connect Bullhorn',
      action: callbacks.onConnectBullhorn || (() => console.log('Connect Bullhorn')),
      icon: 'Cable',
      priority: 1,
      showInStates: ['new_user', 'empty_state', 'growth_mode'],
      dismissalType: 'permanent',
    },

    // Priority 1b: Connect JobAdder (highest priority for new user onboarding)
    {
      id: 'connect-jobadder',
      type: 'primary',
      title: 'Connect JobAdder',
      description: 'Import your roles and candidates from JobAdder to get started quickly',
      actionLabel: 'Connect JobAdder',
      action: callbacks.onConnectJobAdder || (() => console.log('Connect JobAdder')),
      icon: 'Cable',
      priority: 1,
      showInStates: ['new_user', 'empty_state', 'growth_mode'],
      dismissalType: 'permanent',
    },

    // Priority 1.5: Complete Profile (helps with better matching and personalization)
    {
      id: 'complete-profile',
      type: 'primary',
      title: 'Complete Your Profile',
      description: 'Add your name and contact details to personalize your experience',
      actionLabel: 'Update Profile',
      action: callbacks.onCompleteProfile || (() => console.log('Complete Profile')),
      icon: 'User',
      priority: 1.5,
      showInStates: ['new_user', 'empty_state', 'growth_mode'],
      dismissalType: 'temporary',
    },

    // Priority 2: Add First Role
    {
      id: 'add-first-role',
      type: 'primary',
      title: 'Add Your First Role',
      description: 'Create a role to start finding the perfect candidate match',
      actionLabel: 'Add Role',
      action: callbacks.onAddRole || (() => console.log('Add Role')),
      icon: 'Briefcase',
      priority: 2,
      showInStates: ['new_user', 'empty_state'],
      dismissalType: 'temporary',
    },

    // Priority 3: Add First Candidate
    {
      id: 'add-first-candidate',
      type: 'primary',
      title: 'Add Your First Candidate',
      description: 'Build your talent pool by adding candidates to match with open roles',
      actionLabel: 'Add Candidate',
      action: callbacks.onAddCandidate || (() => console.log('Add Candidate')),
      icon: 'UserPlus',
      priority: 3,
      showInStates: ['new_user', 'empty_state'],
      dismissalType: 'temporary',
    },

    // Priority 3.5: Set Industry Preferences (improves matching quality)
    {
      id: 'set-preferences',
      type: 'primary',
      title: 'Set Your Industry Preferences',
      description: 'Choose your preferred industries and regions for better matching',
      actionLabel: 'Set Preferences',
      action: callbacks.onSetPreferences || (() => console.log('Set Preferences')),
      icon: 'Settings',
      priority: 3.5,
      showInStates: ['new_user', 'empty_state', 'growth_mode'],
      dismissalType: 'temporary',
    },

    // Priority 4: Guidance for non-admins (can't connect ATS)
    {
      id: 'ats-guidance',
      type: 'guidance',
      title: 'ATS Connection Required',
      description: 'Ask your account holder or admin to connect your ATS system to import roles and candidates',
      actionLabel: '',
      action: () => {}, // No action for guidance cards
      icon: 'Info',
      priority: 4,
      showInStates: ['new_user', 'empty_state'],
      dismissalType: 'session',
    },

    // Priority 5: Reply to unread messages (EXCLUDED from new_user - only for active users)
    {
      id: 'reply-messages',
      type: 'primary',
      title: 'Reply to Pending Messages',
      description: 'You have unread messages from partners that need your response',
      actionLabel: 'View Messages',
      action: callbacks.onReplyMessages || (() => console.log('Reply Messages')),
      icon: 'MessageSquare',
      priority: 5,
      showInStates: ['growth_mode', 'established_user'], // NOT shown in new_user or empty_state
      dismissalType: 'session',
    },

    // Priority 6: Review new matches (EXCLUDED from new_user - only for active users)
    {
      id: 'review-matches',
      type: 'primary',
      title: 'Review New Matches',
      description: 'You have new candidate matches waiting for your review',
      actionLabel: 'View Matches',
      action: callbacks.onReviewMatches || (() => console.log('Review Matches')),
      icon: 'Users',
      priority: 6,
      showInStates: ['growth_mode', 'established_user'], // NOT shown in new_user or empty_state
      dismissalType: 'session',
    },

    // Priority 7: Share first role (EXCLUDED from new_user - for established users)
    {
      id: 'share-first-role',
      type: 'secondary',
      title: 'Share a Role on the Marketplace',
      description: 'Increase your reach by sharing roles with partners across the network',
      actionLabel: 'Share Role',
      action: callbacks.onShareRole || (() => console.log('Share Role')),
      icon: 'Share2',
      priority: 7,
      showInStates: ['growth_mode', 'established_user'], // NOT shown in new_user or empty_state
      dismissalType: 'permanent',
    },

    // Priority 7.5: Explore Marketplace (discover opportunities from partners)
    {
      id: 'explore-marketplace',
      type: 'secondary',
      title: 'Explore the Marketplace',
      description: 'Discover roles and candidates shared by partners in your network',
      actionLabel: 'Browse Marketplace',
      action: callbacks.onExploreMarketplace || (() => console.log('Explore Marketplace')),
      icon: 'Globe',
      priority: 7.5,
      showInStates: ['growth_mode', 'established_user'],
      dismissalType: 'permanent',
    },

    // Priority 8: Invite collaborators (EXCLUDED from new_user - for established users)
    {
      id: 'invite-user',
      type: 'secondary',
      title: 'Invite Team Members',
      description: 'Expand your team by inviting colleagues to collaborate on Role Relay',
      actionLabel: 'Invite Users',
      action: callbacks.onInviteUser || (() => console.log('Invite User')),
      icon: 'UserPlus2',
      priority: 8,
      showInStates: ['growth_mode', 'established_user'], // NOT shown in new_user or empty_state
      dismissalType: 'permanent',
    },

    // Priority 9: Take Product Tour (learn platform features)
    {
      id: 'take-tour',
      type: 'secondary',
      title: 'Take a Quick Tour',
      description: 'Learn how to make the most of Role Relay with a guided walkthrough',
      actionLabel: 'Start Tour',
      action: callbacks.onTakeTour || (() => console.log('Take Tour')),
      icon: 'Info',
      priority: 9,
      showInStates: ['new_user', 'empty_state'],
      dismissalType: 'permanent',
    },

    // Priority 10: View Help Documentation (educational resource)
    {
      id: 'view-help',
      type: 'secondary',
      title: 'Need Help Getting Started?',
      description: 'Check out our help center for guides, tips, and best practices',
      actionLabel: 'View Help',
      action: callbacks.onViewHelp || (() => console.log('View Help')),
      icon: 'HelpCircle',
      priority: 10,
      showInStates: ['new_user', 'empty_state', 'growth_mode'],
      dismissalType: 'permanent',
    },
  ];
}

/**
 * Filter CTAs based on user context and conditions
 */
export function filterCTAsByConditions(
  ctas: CTACard[],
  context: UserContext,
  metrics: DashboardMetrics
): CTACard[] {
  return ctas.filter((cta) => {
    // Check Bullhorn-specific conditions
    if (cta.id === 'connect-bullhorn') {
      return context.capabilities.canConnectAts && !context.hasBullhornConnection;
    }

    // Check JobAdder-specific conditions
    if (cta.id === 'connect-jobadder') {
      return context.capabilities.canConnectAts && !context.hasJobAdderConnection;
    }

    // Check add role conditions
    if (cta.id === 'add-first-role') {
      return metrics.totalRoles === 0;
    }

    // Check add candidate conditions
    if (cta.id === 'add-first-candidate') {
      return metrics.totalCandidates === 0;
    }

    // Check complete profile conditions
    if (cta.id === 'complete-profile') {
      const profile = context.userProfile;
      const isIncomplete =
        !profile?.firstName ||
        !profile?.lastName ||
        !profile?.phoneNumber;
      return isIncomplete;
    }

    // Check set preferences conditions
    if (cta.id === 'set-preferences') {
      const prefs = context.userProfile?.industryPreferences;
      const hasNoPreferences =
        !prefs ||
        (!prefs.regions?.length && !prefs.industries?.length && !prefs.localityIds?.length);
      return hasNoPreferences;
    }

    // Check explore marketplace conditions
    if (cta.id === 'explore-marketplace') {
      const marketplaceEnabled = context.userProfile?.company?.settings?.features?.communityOnly !== true;
      // Show if marketplace is enabled and user has some roles or candidates
      return marketplaceEnabled && (metrics.totalRoles > 0 || metrics.totalCandidates > 0);
    }

    // Check take tour conditions (always available for new users)
    if (cta.id === 'take-tour') {
      return true;
    }

    // Check view help conditions (always available)
    if (cta.id === 'view-help') {
      return true;
    }

    // Check guidance conditions (non-admins without ATS)
    if (cta.id === 'ats-guidance') {
      return (
        !context.capabilities.canConnectAts &&
        !context.hasAtsConnection &&
        metrics.totalRoles === 0 &&
        metrics.totalCandidates === 0
      );
    }

    // Check message conditions
    if (cta.id === 'reply-messages') {
      return metrics.unreadMessages > 0;
    }

    // Check matches conditions
    if (cta.id === 'review-matches') {
      return metrics.newMatches > 0;
    }

    // Check share role conditions
    if (cta.id === 'share-first-role') {
      return (
        context.capabilities.canShareRoles && metrics.totalRoles > 0 && !context.hasSharedRoles
      );
    }

    // Check invite conditions
    if (cta.id === 'invite-user') {
      return (
        context.capabilities.canInviteUsers && context.hasSharedRoles && metrics.totalRoles >= 3
      );
    }

    return true;
  });
}

/**
 * Select CTAs to display based on user context, metrics, and dismissals
 */
export function selectCTAsToDisplay(
  context: UserContext,
  metrics: DashboardMetrics,
  ctaDefinitions: CTACard[],
  maxCTAs: number = 3
): CTACard[] {
  return ctaDefinitions
    // Filter: Check if CTA should show in current state
    .filter((cta) => cta.showInStates.includes(context.state))
    // Filter: Check conditions are met
    .filter((cta) => {
      const filtered = filterCTAsByConditions([cta], context, metrics);
      return filtered.length > 0;
    })
    // Filter: Check if not dismissed
    .filter((cta) => !isDismissed(cta.id))
    // Sort by priority (lower number = higher priority)
    .sort((a, b) => a.priority - b.priority)
    // Take top maxCTAs
    .slice(0, maxCTAs);
}

/**
 * Check metrics and auto-complete actions that have been accomplished
 */
export function checkAndCompleteActions(
  metrics: DashboardMetrics,
  connections: any[],
  completedActions: string[],
  userProfile?: any
): string[] {
  const newCompletions: string[] = [];

  // Check if user added first role
  if (!completedActions.includes('add_first_role') && metrics.totalRoles > 0) {
    dismissCTA('add-first-role', 'permanent');
    newCompletions.push('add_first_role');
  }

  // Check if user added first candidate
  if (!completedActions.includes('add_first_candidate') && metrics.totalCandidates > 0) {
    dismissCTA('add-first-candidate', 'permanent');
    newCompletions.push('add_first_candidate');
  }

  // Check if user connected Bullhorn
  const hasBullhornConnection = connections.some(
    (c: any) => c.provider?.toLowerCase() === 'bullhorn' && c.status === 1
  );
  if (!completedActions.includes('connect_bullhorn') && hasBullhornConnection) {
    dismissCTA('connect-bullhorn', 'permanent');
    newCompletions.push('connect_bullhorn');
  }

  // Check if user connected JobAdder
  const hasJobAdderConnection = connections.some(
    (c: any) => c.provider?.toLowerCase() === 'jobadder' && c.status === 1
  );
  if (!completedActions.includes('connect_jobadder') && hasJobAdderConnection) {
    dismissCTA('connect-jobadder', 'permanent');
    newCompletions.push('connect_jobadder');
  }

  // Check if user shared first role
  if (!completedActions.includes('share_first_role') && metrics.publicSharedRoles > 0) {
    dismissCTA('share-first-role', 'permanent');
    newCompletions.push('share_first_role');
  }

  // Check if user completed profile
  if (userProfile) {
    const isProfileComplete =
      userProfile.firstName &&
      userProfile.lastName &&
      userProfile.phoneNumber;
    if (!completedActions.includes('complete_profile') && isProfileComplete) {
      dismissCTA('complete-profile', 'permanent');
      newCompletions.push('complete_profile');
    }

    // Check if user set preferences
    const prefs = userProfile.industryPreferences;
    const hasPreferences =
      prefs &&
      (prefs.regions?.length > 0 || prefs.industries?.length > 0 || prefs.localityIds?.length > 0);
    if (!completedActions.includes('set_preferences') && hasPreferences) {
      dismissCTA('set-preferences', 'permanent');
      newCompletions.push('set_preferences');
    }
  }

  return newCompletions;
}

/**
 * Get dynamic CTA text based on metrics
 */
export function getDynamicCTAText(
  ctaId: string,
  metrics: DashboardMetrics
): Partial<CTACard> | null {
  if (ctaId === 'reply-messages' && metrics.unreadMessages > 0) {
    return {
      title: `Reply to ${metrics.unreadMessages} Message${metrics.unreadMessages === 1 ? '' : 's'}`,
    };
  }

  if (ctaId === 'review-matches' && metrics.newMatches > 0) {
    return {
      title: `Review ${metrics.newMatches} New Match${metrics.newMatches === 1 ? '' : 'es'}`,
    };
  }

  return null;
}
